import React, { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/common";
import { toast } from "sonner";
import { Loader2, Send, Paperclip, X, Users } from "lucide-react";

const inputCls = "w-full px-3.5 py-2.5 rounded-md border border-slate-300 bg-white focus:border-[#00427A] focus:ring-2 focus:ring-[#00427A]/20 outline-none";
const Field = ({ label, children }) => (
  <div><label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>{children}</div>
);

export default function PostOpportunity() {
  const { user, meta } = useAuth();
  const [form, setForm] = useState({
    title: "", type: "RFQ", description: "",
    mine_name: user.mine_name || "", districts: [], towns: [], sectors: [],
    closing_date: "", closing_time: "", broadcast_all: false,
  });
  const [attachment, setAttachment] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reach, setReach] = useState(null);
  const [reachLoading, setReachLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    setReachLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/opportunities/preview-reach", {
          districts: form.districts, towns: form.towns, sectors: form.sectors,
          broadcast_all: form.broadcast_all,
        });
        setReach(data);
      } catch {
        setReach(null);
      } finally {
        setReachLoading(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [form.districts, form.towns, form.sectors, form.broadcast_all]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let attachment_id = null;
      let attachment_extra = {};
      if (attachment) {
        const fd = new FormData();
        fd.append("file", attachment);
        const { data } = await api.post("/opportunities/attachment", fd, { headers: { "Content-Type": "multipart/form-data" } });
        attachment_id = data.file_id;
        attachment_extra = { attachment_path: data.storage_path, attachment_name: data.original_filename };
      }
      const { data } = await api.post("/opportunities", { ...form, attachment_id, ...attachment_extra });
      toast.success(`Opportunity posted · notified ${data.notified_count} SMMEs by email`);
      setForm({ title: "", type: "RFQ", description: "", mine_name: user.mine_name || "", districts: [], towns: [], sectors: [], closing_date: "", closing_time: "", broadcast_all: false });
      setAttachment(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Buyer" title="Post Opportunity"
        subtitle="Create an RFQ, Tender, RFI or RFP. Targeted SMMEs are notified by email." />
      <form onSubmit={submit} className="bg-white rounded-xl border border-slate-200 p-6 sm:p-8 space-y-6 max-w-5xl">
        <Field label="Opportunity Title">
          <input data-testid="post-title-input" className={inputCls} value={form.title} onChange={set("title")} required placeholder="e.g. Supply of conveyor components" />
        </Field>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Opportunity Type">
            <select data-testid="post-type-select" className={inputCls} value={form.type} onChange={set("type")}>
              {(meta?.opportunity_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Name of Mine">
            <div className="max-h-52 overflow-y-auto space-y-1.5 border border-slate-300 rounded-md p-3 bg-white">
              {(meta?.mines || []).map((m) => (
                <label key={m} data-testid={`buyer-mine-checkbox-${m}`}
                  className={`flex items-center gap-3 px-3 py-2 rounded-md border cursor-pointer transition-colors ${
                    form.mine_name === m ? "border-[#00427A] bg-[#00427A]/5" : "border-transparent hover:bg-slate-50"
                  }`}>
                  <input type="checkbox" className="h-4 w-4 accent-[#00427A]"
                    checked={form.mine_name === m}
                    onChange={() => setForm({ ...form, mine_name: form.mine_name === m ? "" : m })} />
                  <span className="text-sm text-slate-800">{m}</span>
                </label>
              ))}
            </div>
          </Field>
        </div>
        <Field label="Description">
          <textarea data-testid="post-description-input" className={inputCls} rows={5} value={form.description} onChange={set("description")} required placeholder="Scope, requirements, specifications…" />
        </Field>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Closing Date">
            <input data-testid="post-closing-date" type="date" className={inputCls} value={form.closing_date} onChange={set("closing_date")} />
          </Field>
          <Field label="Closing Time">
            <input data-testid="post-closing-time" type="time" className={inputCls} value={form.closing_time} onChange={set("closing_time")} />
          </Field>
        </div>

        <Field label="Target District (select one or both)">
          <div className="grid sm:grid-cols-2 gap-1.5 border border-slate-300 rounded-md p-3 bg-white">
            {(meta?.districts || []).map((d) => {
              const checked = (form.districts || []).includes(d);
              return (
                <label key={d} data-testid={`post-district-checkbox-${d}`}
                  className={`flex items-center gap-3 px-3 py-2 rounded-md border cursor-pointer transition-colors ${
                    checked ? "border-[#00427A] bg-[#00427A]/5" : "border-slate-200 hover:bg-slate-50"
                  }`}>
                  <input type="checkbox" className="h-4 w-4 shrink-0 accent-[#00427A]" checked={checked}
                    onChange={() => {
                      const cur = form.districts || [];
                      setForm({ ...form, districts: checked ? cur.filter((x) => x !== d) : [...cur, d] });
                    }} />
                  <span className="text-sm text-slate-800">{d}</span>
                </label>
              );
            })}
          </div>
          <p className="text-xs text-slate-400 pt-1">Leave both unchecked to target all districts.</p>
        </Field>

        <Field label="Target Sectors (select any)">
          <div className="max-h-64 overflow-y-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5 border border-slate-300 rounded-md p-3 bg-white">
            {(meta?.sectors || []).map((s) => {
              const checked = (form.sectors || []).includes(s);
              return (
                <label key={s} data-testid={`post-sector-checkbox-${s}`}
                  className={`flex items-start gap-2.5 px-3 py-2 rounded-md border cursor-pointer transition-colors ${
                    checked ? "border-[#00427A] bg-[#00427A]/5" : "border-slate-200 hover:bg-slate-50"
                  }`}>
                  <input type="checkbox" className="h-4 w-4 mt-0.5 shrink-0 accent-[#00427A]" checked={checked}
                    onChange={() => {
                      const cur = form.sectors || [];
                      setForm({ ...form, sectors: checked ? cur.filter((x) => x !== s) : [...cur, s] });
                    }} />
                  <span className="text-sm text-slate-800 leading-snug">{s}</span>
                </label>
              );
            })}
          </div>
          <p className="text-xs text-slate-400 pt-1">Leave all unchecked to target every sector.</p>
        </Field>

        <Field label="Target Towns / Villages (select any)">
          <div className="max-h-56 overflow-y-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5 border border-slate-300 rounded-md p-3 bg-white">
            {(meta?.towns || []).map((t) => {
              const checked = (form.towns || []).includes(t);
              return (
                <label key={t} data-testid={`post-town-checkbox-${t}`}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-md border cursor-pointer transition-colors ${
                    checked ? "border-[#00427A] bg-[#00427A]/5" : "border-transparent hover:bg-slate-50"
                  }`}>
                  <input type="checkbox" className="h-4 w-4 accent-[#00427A]" checked={checked}
                    onChange={() => {
                      const cur = form.towns || [];
                      setForm({ ...form, towns: checked ? cur.filter((x) => x !== t) : [...cur, t] });
                    }} />
                  <span className="text-sm text-slate-800">{t}</span>
                </label>
              );
            })}
          </div>
          <p className="text-xs text-slate-400 pt-1">Leave all unchecked to target every town/village.</p>
        </Field>

        <div>
          <label data-testid="post-attachment-label" className="inline-flex items-center gap-2 text-sm font-semibold cursor-pointer border border-slate-300 hover:bg-slate-50 px-4 py-2 rounded-md">
            <Paperclip className="h-4 w-4" /> {attachment ? "Change attachment" : "Attach specification (optional)"}
            <input data-testid="post-attachment-input" type="file" className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg" onChange={(e) => setAttachment(e.target.files[0])} />
          </label>
          {attachment && (
            <span className="ml-3 text-sm text-slate-600 inline-flex items-center gap-1">
              {attachment.name}
              <button type="button" onClick={() => setAttachment(null)}><X className="h-3.5 w-3.5 text-slate-400 hover:text-red-500" /></button>
            </span>
          )}
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-md px-4 py-3">
          <input data-testid="buyer-broadcast-all-checkbox" type="checkbox" checked={form.broadcast_all}
            onChange={(e) => setForm({ ...form, broadcast_all: e.target.checked })} className="h-4 w-4 accent-[#00427A]" />
          <span><strong>Broadcast to all SMMEs</strong> — ignore district & sector targeting and notify every registered supplier.</span>
        </label>

        <div data-testid="reach-preview" className="flex items-center gap-3 rounded-md border border-[#00427A]/20 bg-[#00427A]/5 px-4 py-3">
          <Users className="h-5 w-5 text-[#00427A] shrink-0" />
          <p className="text-sm text-slate-700">
            {reachLoading ? (
              <span className="inline-flex items-center gap-2 text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Calculating reach…</span>
            ) : reach ? (
              <>
                <strong data-testid="reach-count" className="text-[#00427A]">{reach.matched}</strong> of {reach.total} registered SMME{reach.total === 1 ? "" : "s"} will be notified by email
                {form.broadcast_all ? " (broadcast to all)." : " based on your targeting."}
              </>
            ) : (
              <span className="text-slate-500">Notification reach will appear here.</span>
            )}
          </p>
        </div>

        <div className="flex justify-end">
          <button data-testid="buyer-post-opportunity-button" disabled={saving}
            className="inline-flex items-center gap-2 bg-[#00427A] hover:bg-[#00325C] text-white font-semibold px-6 py-2.5 rounded-md transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Post Opportunity
          </button>
        </div>
      </form>
    </div>
  );
}
